import React, { useImperativeHandle, forwardRef, useRef, useEffect, useCallback } from 'react';
import Button from '../Button/Button';
import InputField from '../InputField/InputField';
import { FaCheckCircle, FaInfoCircle, FaTimesCircle } from 'react-icons/fa';
import { FaCloudUploadAlt } from 'react-icons/fa'; // adicione ao topo
import UploadFileList from './UploadFileList';
import Toast from '../Toast/Toast'; // Importa o Toast
import Tooltip from '../Tooltip/Tooltip'; // adicione este import
import { useUploadState } from "./useUploadState";
import { useUploadI18n } from "./UploadI18nContext";
import clsx from "clsx";
import { z } from "zod";

/**
 * Componente de upload de arquivos com suporte avançado e altamente customizável.
 *
 * **Funcionalidades:**
 * - Seleção de arquivos via input ou drag-and-drop.
 * - Suporte a múltiplos arquivos e seleção de diretórios (webkitdirectory).
 * - Conversão automática para WebP (opcional) e processamento customizado de arquivos.
 * - Upload automático ou manual, com barra de progresso individual e total.
 * - Feedback visual e acessível para estados de erro, sucesso e progresso.
 * - Mensagens customizáveis e internacionalização (i18n).
 * - Lista de arquivos agrupada por pastas (quando aplicável).
 * - Suporte a remoção, seleção múltipla, desfazer remoção e reordenação via drag-and-drop.
 * - Drag-out/download: arraste arquivos para fora para baixar/copiar.
 * - Acessibilidade: roles, aria-*, navegação por teclado, leitura por leitores de tela.
 * - Customização de ícones, temas (light, dark, white) e estilos.
 * - Suporte a arquivos controlados externamente (modo controlado).
 * - Toasts para erros de duplicidade, remoção e arquivos grandes.
 * - Barra de progresso global sticky no topo.
 * - Suporte a componentes internos customizados (FileItem, Folder, ProgressBar).
 * - Suporte a nomes de classe customizados para estilização avançada.
 * - Suporte a callbacks para download e exclusão backend.
 * - Suporte a renomeação inline de arquivos.
 * - Suporte a undo/redo de remoção.
 *
 * @component
 * @example
 * <Upload
 *   onFilesSelected={(files) => console.log(files)}
 *   accept={['image/png', 'image/jpeg']}
 *   maxFiles={3}
 *   maxFileSize={2 * 1024 * 1024}
 *   multiple
 *   label="Arraste ou clique para enviar imagens"
 *   buttonTitle="Selecionar imagens"
 *   autoUpload
 *   onUpload={async (file, onProgress, signal) => { ... }}
 *   convertToWebP={false}
 *   onProcessFile={async (file) => { ... }}
 *   tooltip="Apenas imagens PNG ou JPEG"
 *   helperText="Arraste ou clique para enviar imagens"
 *   errorMessage="Arquivo inválido"
 *   successMessage="Upload realizado com sucesso!"
 *   i18nMessages={{
 *     getDefaultErrorMessage: () => "Arquivo inválido.",
 *     getDefaultSuccessMessage: () => "Upload concluído!",
 *   }}
 *   containerProps={{ style: { border: '1px solid #ccc' } }}
 *   inputProps={{ tabIndex: 0 }}
 *   customValidation={(files) => files.length > 3 ? "Máximo 3 arquivos" : undefined }
 *   loadingMessage="Enviando arquivos..."
 *   theme="dark"
 *   directory
 *   data-testid="upload-container"
 *   SuccessIcon={CustomSuccessIcon}
 *   ErrorIcon={CustomErrorIcon}
 *   RemoveIcon={CustomRemoveIcon}
 *   InfoIcon={CustomInfoIcon}
 *   selectedFiles={filesExternos}
 *   components={{
 *     FileItem: CustomFileItem,
 *     Folder: CustomFolder,
 *     ProgressBar: CustomProgressBar,
 *   }}
 *   classNames={{
 *     container: "my-upload-container",
 *     label: "my-upload-label",
 *     button: "my-upload-btn",
 *     fileItem: "my-upload-file-item",
 *     folder: "my-upload-folder",
 *     progressBar: "my-upload-progress-bar",
 *   }}
 *   onDownload={async ({ file, index }) => { ... }}
 *   onDelete={async ({ file, index }) => { ... }}
 *   onStatusChange={({ type, message, progress, files }) => { ... }}
 *   onPrepareUpload={async (file) => ({ url: "/upload", headers: { Authorization: "Bearer ..." } })}
 * />
 *
 * @typedef {Object} UploadFileWithPath
 * @property {File} file - O arquivo selecionado.
 * @property {string} [relativePath] - Caminho relativo do arquivo (útil para uploads de diretórios).
 * @property {string} [customName] - Nome customizado (renomeação inline).
 *
 * @typedef {Object} UploadImperativeHandle
 * @property {() => void} open - Abre o seletor de arquivos programaticamente.
 * @property {() => void} clear - Limpa todos os arquivos selecionados.
 * @property {() => UploadFileWithPath[]} getFiles - Retorna os arquivos atualmente selecionados.
 * @property {(files: UploadFileWithPath[]) => void} setFiles - Define os arquivos selecionados (apenas modo não controlado).
 * @property {() => void} reset - Limpa arquivos, erros, progresso, sucesso, undo, etc.
 *
 * @typedef {Object} UploadProps
 * @property {(files: UploadFileWithPath[], error?: Error | string | null) => void} onFilesSelected - Callback chamado ao selecionar arquivos (após validação e processamento).
 * @property {string | string[]} [accept] - Tipos MIME ou extensões aceitas (ex: 'image/*', ['image/png', 'image/jpeg']).
 * @property {number} [maxFiles=1] - Número máximo de arquivos permitidos.
 * @property {boolean} [multiple=false] - Permite seleção múltipla de arquivos.
 * @property {string} [label] - Texto exibido na área de upload.
 * @property {boolean} [disabled=false] - Desabilita o componente.
 * @property {string} [buttonTitle] - Texto do botão de seleção.
 * @property {boolean} [autoUpload=false] - Se verdadeiro, faz upload automático ao selecionar arquivos.
 * @property {(file: File, onProgress: (percent: number) => void, signal?: AbortSignal) => Promise<void>} [onUpload] - Função de upload customizada, chamada para cada arquivo. Recebe callback de progresso e signal para cancelamento.
 * @property {boolean} [convertToWebP=true] - Se verdadeiro, converte imagens para WebP antes de processar.
 * @property {(file: File) => Promise<File>} [onProcessFile] - Callback customizado para processar cada arquivo antes de adicionar.
 * @property {string} [tooltip] - Texto de dica acessível (ícone de informação).
 * @property {string} [helperText] - Texto auxiliar exibido abaixo do campo.
 * @property {string|((files: File[]) => string)} [errorMessage] - Mensagem de erro customizada (string ou função).
 * @property {string|((files: File[]) => string)} [successMessage] - Mensagem de sucesso customizada (string ou função).
 * @property {object} [i18nMessages] - Mensagens padrão customizadas (ex: getDefaultErrorMessage, getDefaultSuccessMessage).
 * @property {object} [containerProps] - Props extras para o container externo (ex: style, theme, size, fullWidth, tooltip, buttonProps).
 * @property {object} [inputProps] - Props extras para o input de arquivo.
 * @property {(files: File[]) => string|undefined} [customValidation] - Validação customizada dos arquivos (retorne string para erro).
 * @property {number} [maxFileSize] - Tamanho máximo permitido para cada arquivo (em bytes).
 * @property {string} [loadingMessage] - Mensagem exibida durante o upload/processamento.
 * @property {'light'|'dark'|'white'} [theme='light'] - Tema visual do componente.
 * @property {string} [data-testid] - Test id customizado para o container.
 * @property {boolean} [directory] - Permite seleção de diretórios (webkitdirectory).
 * @property {React.ComponentType<{ className?: string; style?: React.CSSProperties }>} [SuccessIcon] - Ícone customizado para sucesso.
 * @property {React.ComponentType<{ className?: string; style?: React.CSSProperties }>} [ErrorIcon] - Ícone customizado para erro.
 * @property {React.ComponentType<{ className?: string; style?: React.CSSProperties }>} [RemoveIcon] - Ícone customizado para remoção.
 * @property {React.ComponentType<{ className?: string; style?: React.CSSProperties }>} [InfoIcon] - Ícone customizado para informação.
 * @property {UploadFileWithPath[]} [selectedFiles] - Lista de arquivos controlada externamente (modo controlado).
 * @property {object} [components] - Componentes internos customizados (FileItem, Folder, ProgressBar).
 * @property {object} [classNames] - Nomes de classe customizados para estilização avançada.
 * @property {(file: File) => Promise<{url: string, headers?: Record<string, string>}>} [uploadEndpointResolver] - Função que retorna endpoint e headers para upload dinâmico.
 * @property {(args: {file: File, index?: number, response?: any}) => Promise<void>} [onDownload] - Callback chamado ao clicar para baixar um arquivo. Se não definido, faz download local.
 * @property {(args: {file: File, index?: number, response?: any}) => Promise<void>} [onDelete] - Callback chamado ao clicar para excluir um arquivo do backend. Após sucesso, remove localmente.
 * @property {(args: {status: string, selectedFiles: UploadFileWithPath[], error: any, success: any}) => void} [onStateChange] - Callback chamado sempre que o estado principal do upload muda.
 * @property {(args: {type: 'error'|'success'|'info'|'progress', message: string, progress?: number, files?: UploadFileWithPath[]}) => void} [onStatusChange] - Callback chamado para mensagens de status (erro, sucesso, progresso, info).
 * @property {(file: File) => Promise<{url: string, headers?: Record<string, string>}>} [onPrepareUpload] - Função opcional para preparar o upload de cada arquivo (endpoint dinâmico).
 *
 * @returns {JSX.Element} Componente de upload de arquivos com suporte avançado.
 */
// 1. Define the UploadProps interface above the component
interface UploadMessages {
  remove?: string;
  cancel?: string;
  upload?: string;
  uploading?: string;
  selectAll?: string;
  deselectAll?: string;
  removeSelected?: string;
  clearAll?: string;
  undo?: string;
  fileRemoved?: string;
  filesRemoved?: string;
  directoryNotSupported?: string;
  largeFileWarning?: string;
  defaultError?: string;
  defaultSuccess?: string;
  // Adicione outros textos conforme necessário
}



// 1. Defina o schema Zod para as props principais
const UploadPropsSchema = z.object({
  onFilesSelected: z.function().args(z.array(z.object({
    file: z.instanceof(File),
    relativePath: z.string().optional(),
  })), z.union([z.instanceof(Error), z.string(), z.null()]).optional()).returns(z.void()),
  accept: z.union([z.string(), z.array(z.string())]).optional(),
  maxFiles: z.number().optional(),
  multiple: z.boolean().optional(),
  label: z.string().optional(),
  disabled: z.boolean().optional(),
  buttonTitle: z.string().optional(),
  autoUpload: z.boolean().optional(),
  onUpload: z.function().args(
    z.instanceof(File),
    z.function().args(z.number()).returns(z.void()),
    z.instanceof(AbortSignal).optional()
  ).returns(z.promise(z.void())).optional(),
  convertToWebP: z.boolean().optional(),
  onProcessFile: z.function().args(z.instanceof(File)).returns(z.promise(z.instanceof(File))).optional(),
  tooltip: z.string().optional(),
  helperText: z.string().optional(),
  errorMessage: z.union([z.string(), z.function().args(z.array(z.instanceof(File))).returns(z.string())]).optional(),
  successMessage: z.union([z.string(), z.function().args(z.array(z.instanceof(File))).returns(z.string())]).optional(),
  i18nMessages: z.object({
    getDefaultErrorMessage: z.function().args(z.array(z.instanceof(File))).returns(z.string()).optional(),
    getDefaultSuccessMessage: z.function().args(z.array(z.instanceof(File))).returns(z.string()).optional(),
  }).optional(),
  containerProps: z.any().optional(),
  inputProps: z.any().optional(),
  customValidation: z.function().args(z.array(z.instanceof(File))).returns(z.union([z.string(), z.undefined()])).optional(),
  maxFileSize: z.number().optional(),
  loadingMessage: z.string().optional(),
  theme: z.enum(['light', 'dark', 'white']).optional(),
  "data-testid": z.string().optional(),
  directory: z.boolean().optional(),
  SuccessIcon: z.any().optional(),
  ErrorIcon: z.any().optional(),
  RemoveIcon: z.any().optional(),
  InfoIcon: z.any().optional(),
  selectedFiles: z.array(z.object({
    file: z.instanceof(File),
    relativePath: z.string().optional(),
  })).optional(),
  components: z.any().optional(),
  classNames: z.any().optional(),
  /**
   * Função que recebe um arquivo e retorna o endpoint e headers para upload.
   * Útil para múltiplos endpoints por tipo de arquivo.
   */
  uploadEndpointResolver: z.function()
    .args(z.instanceof(File))
    .returns(z.object({
      url: z.string(),
      headers: z.record(z.string(), z.string()).optional(),
    }))
    .optional(),
  // PATCH: nova prop para integração com campos externos
  onUploadComplete: z.function()
    .args(
      z.object({
        file: z.instanceof(File),
        response: z.any().optional(), // resposta do backend (ex: url, path, etc.)
        index: z.number().optional(), // índice do arquivo na lista
      })
    )
    .returns(z.void())
    .optional(),
  // Adicione a prop onStateChange
  onStateChange: z.function()
    .args(
      z.object({
        status: z.string(),
        selectedFiles: z.array(z.object({
          file: z.instanceof(File),
          relativePath: z.string().optional(),
        })),
        error: z.any(),
        success: z.any(),
      })
    )
    .returns(z.void())
    .optional(),
  // 1. Acrescente as novas props ao schema Zod:
  onDownload: z.function()
    .args(z.object({
      file: z.instanceof(File),
      index: z.number().optional(),
      response: z.any().optional(), // info extra, ex: url
    }))
    .returns(z.promise(z.void()))
    .optional(),
  onDelete: z.function()
    .args(z.object({
      file: z.instanceof(File),
      index: z.number().optional(),
      response: z.any().optional(),
    }))
    .returns(z.promise(z.void()))
    .optional(),
  /**
   * Callback chamado sempre que houver uma mensagem de status relevante (erro, sucesso, progresso, info).
   * Recebe: { type: 'error'|'success'|'info'|'progress', message: string, progress?: number, files?: UploadFileWithPath[] }
   */
  onStatusChange: z.function()
    .args(z.object({
      type: z.enum(['error', 'success', 'info', 'progress']),
      message: z.string(),
      progress: z.number().optional(),
      files: z.array(z.object({
        file: z.instanceof(File),
        relativePath: z.string().optional(),
      })).optional(),
    }))
    .returns(z.void())
    .optional(),
  /**
   * Função opcional para preparar o upload de cada arquivo.
   * Pode retornar { url, headers } para upload dinâmico.
   */
  onPrepareUpload: z.function()
    .args(z.instanceof(File))
    .returns(
      z.promise(
        z.object({
          url: z.string(),
          headers: z.record(z.string(), z.string()).optional(),
        })
      )
    )
    .optional(),
});

// 2. Use o tipo inferido do Zod para as props
type UploadProps = z.infer<typeof UploadPropsSchema>;

// 2. Use o tipo inferido do Zod para as props
export interface UploadImperativeHandle {
  open: () => void;
  clear: () => void;
  getFiles: () => UploadFileWithPath[];
  setFiles: (files: UploadFileWithPath[]) => void;
  reset: () => void; // PATCH: método imperativo para resetar tudo
}

const Upload = forwardRef<UploadImperativeHandle, UploadProps>((props, ref) => {
  const state = useUploadState(props);
  const { messages, rtl } = useUploadI18n();

  // Ref para focar item restaurado
  const restoredIndexRef = useRef<number | null>(null);

  // Corrija os acessos aos estados e handlers:
  // - Use sempre state.* para acessar handlers, estados e mensagens
  // - Não use variáveis locais como setUndoInfo, setSelectedIndexes, etc.
  // - Use state.undoInfo, state.setTooltipOpen, state.handleUndoRemove, etc.

  // Ícones customizados ou padrão
  const SuccessIconComp = props.SuccessIcon || FaCheckCircle;
  const ErrorIconComp = props.ErrorIcon || FaTimesCircle;
  const RemoveIconComp = props.RemoveIcon || FaTimesCircle;
  const InfoIconComp = props.InfoIcon || FaInfoCircle;

  // Componentes customizados
  const FileItemComp = props.components?.FileItem;
  const FolderComp = props.components?.Folder;
  const ProgressBarComp = props.components?.ProgressBar;

  // Classes customizadas
  const buttonClass = clsx(
    "upload-btn",
    props.classNames?.button
  );

  // Use variáveis do state
  const {
    inputRef,
    inputKey,
    acceptStr,
    loading,
    previewUrls,
    manualUploading,
    abortControllers,
    mergedMessages,
    totalProgress,
    hasLargeFiles,
    undoInfo,
    tooltipId,
    selectedFiles,
    selectedIndexes,
    uploadProgress,
    fileErrors,
    fileSuccess,
    draggedIdx,
    showSuccess,
    effectiveError,
    effectiveSuccess,
    directoryNotSupported,
    errorId,
    successId,
    helperId,
    dragActive,
    handleFilesSelected,
    handleRemoveFile,
    toggleSelectFile,
    selectAllFiles,
    deselectAllFiles,
    handleRemoveSelectedFiles,
    handleClearAllFiles,
    handleManualUpload,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleUndoRemove,
    handleButtonClick,
    handleDrag,
    handleDrop,
  } = state;

  const [touched, setTouched] = React.useState(false);

  useImperativeHandle(ref, () => ({
    open: () => inputRef.current?.click(),
    clear: () => handleClearAllFiles(),
    getFiles: () => [...selectedFiles],
    setFiles: (files: UploadFileWithPath[]) => {
      if (props.selectedFiles === undefined && state.setInternalSelectedFiles) {
        state.setInternalSelectedFiles(files);
        props.onFilesSelected(files, null);
      }
    },
    reset: () => {
      // PATCH: Limpa arquivos, erros, progresso, sucesso, undo, etc.
      handleClearAllFiles();
      state.setUndoInfo(null);
      state.setCustomError(null);
      state.setTooltipOpen(false);
      // Se quiser, limpe outros estados visuais aqui
    }
  }), [
    inputRef,
    selectedFiles,
    handleClearAllFiles,
    props,
    state
  ]);

  // PATCH: eventos/callbacks para transições de estado
  useEffect(() => {
    if (props.onStateChange) {
      props.onStateChange({
        status: state.manualUploading
          ? "uploading"
          : state.showSuccess
          ? "complete"
          : state.customError
          ? "error"
          : selectedFiles.length === 0
          ? "awaiting-file"
          : "pre-upload",
        selectedFiles,
        error: state.customError,
        success: state.showSuccess,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.manualUploading,
    state.showSuccess,
    state.customError,
    selectedFiles.length
  ]);

  // Handlers estáveis para passar para filhos
  const handleInputChange = useCallback(
    (value: File | File[] | FileList | null) => {
      setTouched(true);
      let files: File[] = [];
      if (value instanceof FileList) {
        files = Array.from(value);
      } else if (Array.isArray(value)) {
        files = value;
      } else if (value instanceof File) {
        files = [value];
      }
      if (files.length > 0) {
        handleFilesSelected(files);
      }
    },
    [handleFilesSelected]
  );

  const handleUploadButtonClick = useCallback(
    () => handleButtonClick(),
    [handleButtonClick]
  );

  const handleManualUploadClick = useCallback(
    () => handleManualUpload(),
    [handleManualUpload]
  );

  const handleCancelUpload = useCallback(
    () => abortControllers.forEach(ctrl => ctrl.abort()),
    [abortControllers]
  );

  const handleSelectAllFiles = useCallback(
    () => selectAllFiles(),
    [selectAllFiles]
  );

  const handleDeselectAllFiles = useCallback(
    () => deselectAllFiles(),
    [deselectAllFiles]
  );

  const handleRemoveSelectedFilesClick = useCallback(
    () => handleRemoveSelectedFiles(),
    [handleRemoveSelectedFiles]
  );

  const handleClearAllFilesClick = useCallback(
    () => handleClearAllFiles(),
    [handleClearAllFiles]
  );

  // PATCH: Exemplo de personalização dos botões conforme status
  // (Você pode expandir isso para permitir renderização customizada via props)
  const renderActionButtons = () => {
    if (selectedFiles.length === 0) return null;
    if (props.autoUpload && props.onUpload) return null;
    if (!props.onUpload) return null;

    if (manualUploading) {
      return (
        <Button
          title={messages.cancel}
          onClick={handleCancelUpload}
          disabled={props.disabled}
          theme={props.theme === "white" ? "light" : props.theme}
          size={props.containerProps?.size || "medium"}
          data-testid="upload-cancel-upload-btn"
          buttonClassName="upload-cancel-upload-btn upload-cancel-upload-btn--danger"
        >
          {messages.cancel}
        </Button>
      );
    }

    // PATCH: botão "Atualizar" se já houve sucesso
    if (showSuccess) {
      return (
        <Button
          title="Atualizar arquivo(s)"
          onClick={handleManualUploadClick}
          disabled={props.disabled}
          theme={props.theme === "white" ? "light" : props.theme}
          size={props.containerProps?.size || "medium"}
          data-testid="upload-reupload-btn"
        >
          Atualizar
        </Button>
      );
    }

    // Botão padrão de upload
    return (
      <Button
        title={manualUploading ? (props.loadingMessage || messages.uploading) : messages.upload}
        onClick={handleManualUploadClick}
        disabled={manualUploading || props.disabled}
        loading={manualUploading}
        theme={props.theme === "white" ? "light" : props.theme}
        size={props.containerProps?.size || "medium"}
        data-testid="upload-manual-upload-btn"
      >
        {messages.upload}
      </Button>
    );
  };

  // Destructure onDownload from props to use as a stable dependency
  const { onDownload } = props;

  // Handler para download
  const handleDownload = useCallback(
    async (fileObj: UploadFileWithPath, idx: number) => {
      if (onDownload) {
        await onDownload({ file: fileObj.file, index: idx, response: fileSuccess[idx] });
      } else {
        // Default: tenta baixar o arquivo local (não do backend)
        const url = URL.createObjectURL(fileObj.file);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileObj.file.name;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 100);
      }
    },
    [onDownload, fileSuccess]
  );

  // Destructure onDelete from props to use as a stable dependency
  const { onDelete } = props;

  // Handler para exclusão backend
  const handleDelete = useCallback(
    async (fileObj: UploadFileWithPath, idx: number) => {
      if (onDelete) {
        await onDelete({ file: fileObj.file, index: idx, response: fileSuccess[idx] });
      }
      // Após exclusão backend, remova localmente
      handleRemoveFile(idx);
    },
    [onDelete, fileSuccess, handleRemoveFile]
  );

  return (
    <div
      data-testid={props["data-testid"]}
      onDragEnter={(e) => handleDrag(e, true)}
      onDragLeave={(e) => handleDrag(e, false)}
      onDragOver={(e) => handleDrag(e, true)}
      onDrop={handleDrop}
      className={clsx(
        "border-2 border-dashed rounded-xl p-5 text-center bg-white transition-all duration-200 cursor-pointer outline-none",
        rtl && "rtl",
        props.disabled && "bg-gray-100 cursor-not-allowed",
        props.theme === "dark" && "bg-[#181c24] border-[#333] text-[#f1f1f1]",
        props.theme === "white" && "bg-white border-[#eee] text-[#222]",
        dragActive && "border-[#009EEB] bg-[#e3f7ff] shadow-[0_0_0_4px_#b3e5fc,0_8px_32px_rgba(0,158,235,0.10)] relative",
        props.classNames?.container
      )}
      dir={rtl ? "rtl" : undefined}
      tabIndex={0}
      role="group"
      aria-disabled={props.disabled}
      aria-label={props.label}
      aria-describedby={
        state.effectiveError
          ? state.errorId
          : state.showSuccess
          ? state.successId
          : props.helperText
          ? state.helperId
          : undefined
      }
      onClick={() => !props.disabled && state.inputRef.current?.click()}
      onKeyDown={(e) => {
        if (props.disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          state.inputRef.current?.click();
        }
        if (e.key === 'Delete' && state.selectedIndexes.length > 0) {
          e.preventDefault();
          state.handleRemoveSelectedFiles();
        }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
          e.preventDefault();
          state.selectAllFiles();
        }
        if (e.key === 'Escape') {
          state.deselectAllFiles();
          state.setTooltipOpen(false);
        }
      }}
      onDoubleClick={e => e.preventDefault()}
      {...props.containerProps}
      style={{ position: 'relative', ...props.containerProps?.style }}
    >
      {/* Barra de progresso global sticky no topo */}
      {(props.autoUpload && props.onUpload && selectedFiles.length > 0) || manualUploading ? (
        <div className="sticky top-0 left-0 right-0 h-[6px] bg-gray-200 rounded-t-lg overflow-hidden z-20 mb-3">
          <div
            className="bg-gradient-to-r from-[#009EEB] to-[#4fc3f7] h-full transition-all duration-500"
            style={{ width: `${totalProgress}%` }}
            role="progressbar"
            aria-valuenow={totalProgress}
            aria-valuemin={0}
            aria-valuemax={100}
            tabIndex={0}
          />
        </div>
      ) : null}

      {dragActive && (
        <div
          className="absolute inset-0 bg-[#009EEB]/15 z-30 flex flex-col items-center justify-center pointer-events-none"
          aria-hidden="true"
        >
          <FaCloudUploadAlt className="text-[80px] text-[#009EEB] mb-4" />
          <div className="text-[22px] text-[#009EEB] font-semibold">
            Solte os arquivos aqui
          </div>
        </div>
      )}

      <div className="hidden">
        <InputField
          key={inputKey}
          ref={inputRef}
          type="file"
          accept={acceptStr}
          multiple={props.multiple}
          value={null}
          onChange={handleInputChange}
          disabled={props.disabled}
          label=""
          data-testid="upload-input"
          // Não permita sobrescrever type, onChange, value e onError via inputProps!
          {...(props.inputProps && {
            ...props.inputProps,
            type: undefined,
            onChange: undefined,
            value: undefined,
            onError: undefined,
          })}
          {...(props.directory ? { webkitdirectory: "true", directory: "true" } : {})} // <-- Adicione esta linha
        />
      </div>

      <div className="flex items-center gap-2">
        <p
          className={clsx(
            "my-2 text-[#333]",
            dragActive && "text-[#009EEB] font-semibold",
            props.theme === "dark" && "text-[#e0e0e0]",
            props.theme === "white" && "text-[#222]",
            props.classNames?.label
          )}
          data-testid="upload-label"
          id={props.tooltip ? tooltipId : undefined}
        >
          {dragActive ? "Solte os arquivos aqui" : props.label}
        </p>
        {props.tooltip && (
          <Tooltip tooltip={props.tooltip}>
            <span
              tabIndex={0}
              aria-label="Informação"
              aria-describedby={tooltipId}
              style={{ cursor: 'pointer', outline: 'none' }}
              role="button"
            >
              <InfoIconComp aria-hidden="true" />
            </span>
          </Tooltip>
        )}
      </div>
      <Tooltip tooltip={props.buttonTitle || messages.upload}>
        <Button
          title={loading ? (props.loadingMessage || messages.uploading) : props.buttonTitle || messages.upload}
          size={props.containerProps?.size || 'medium'}
          onClick={handleUploadButtonClick}
          disabled={props.disabled || loading}
          loading={loading}
          theme={props.theme === "white" ? "light" : props.theme}
          fullWidth={props.containerProps?.fullWidth}
          tooltip={props.containerProps?.tooltip}
          data-testid="upload-button"
          buttonClassName={buttonClass}
          {...(props.containerProps?.buttonProps && {
            ...props.containerProps.buttonProps,
          })}
        />
      </Tooltip>
      {directoryNotSupported && (
        <div
          className="upload-error upload-directory-not-supported"
          role="alert"
          aria-live="assertive"
          data-testid="upload-directory-not-supported"
        >
          {mergedMessages.directoryNotSupported}
        </div>
      )}

      {showSuccess && (
        <div
          role="status"
          aria-live="polite"
          className="upload-success"
          data-testid="upload-success"
          id={successId}
        >
          <SuccessIconComp aria-hidden="true" />
          {effectiveSuccess}
        </div>
      )}

      {effectiveError && touched && (
        <div
          role="alert"
          aria-live="assertive"
          className={clsx(
            "text-red-600 mt-2 text-[0.9em] whitespace-pre-wrap",
            props.theme === "dark" && "text-[#ff7675]",
            props.theme === "white" && "text-[#d32f2f]"
          )}
          data-testid="upload-error"
          id={errorId}
        >
          <ErrorIconComp aria-hidden="true" />
          {effectiveError}
        </div>
      )}

      {props.helperText && !effectiveError && !showSuccess && (
        <div className={clsx(
          "text-[#666] text-[13px]",
          props.theme === "dark" && "text-[#e0e0e0]",
          props.theme === "white" && "text-[#222]"
        )} id={helperId}>
          {props.helperText}
        </div>
      )}

      {selectedFiles.length > 0 && (
        <UploadFileList
          files={selectedFiles}
          previewUrls={previewUrls}
          selectedIndexes={selectedIndexes}
          onToggleSelect={toggleSelectFile}
          onRemove={handleRemoveFile}
          onDownload={handleDownload}
          onDelete={handleDelete}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          RemoveIcon={RemoveIconComp}
          uploadProgress={uploadProgress}
          fileErrors={fileErrors}
          fileSuccess={fileSuccess}
          autoUpload={props.autoUpload}
          onUpload={!!props.onUpload}
          abortControllers={abortControllers}
          manualUploading={manualUploading}
          theme={props.theme}
          messages={mergedMessages}
          draggedIdx={draggedIdx}
          FileItemComponent={FileItemComp}
          FolderComponent={FolderComp}
          ProgressBarComponent={ProgressBarComp}
          classNames={{
            fileItem: props.classNames?.fileItem,
            folder: props.classNames?.folder,
            progressBar: props.classNames?.progressBar,
          }}
          onRenameFile={state.handleRenameFile} // <-- adicione esta linha
        />
      )}

      {selectedFiles.length > 1 && (
        <div className="upload-selected-actions upload-selected-actions-row">
          <Button
            title={mergedMessages.selectAll}
            ariaLabel={mergedMessages.selectAll}
            onClick={handleSelectAllFiles}
            disabled={props.disabled}
            theme={props.theme === "white" ? "light" : props.theme}
            size="small"
            buttonClassName="upload-select-all-btn"
            data-testid="upload-select-all-btn"
          >
            {mergedMessages.selectAll}
          </Button>
          <Button
            title={mergedMessages.deselectAll}
            ariaLabel={mergedMessages.deselectAll}
            onClick={handleDeselectAllFiles}
            disabled={props.disabled}
            theme={props.theme === "white" ? "light" : props.theme}
            size="small"
            buttonClassName="upload-deselect-all-btn"
            data-testid="upload-deselect-all-btn"
          >
            {mergedMessages.deselectAll}
          </Button>
          <Button
            title={mergedMessages.removeSelected}
            ariaLabel={mergedMessages.removeSelected}
            onClick={handleRemoveSelectedFilesClick}
            disabled={props.disabled || selectedIndexes.length === 0}
            theme={props.theme === "white" ? "light" : props.theme}
            size="small"
            buttonClassName="upload-remove-selected-btn"
            data-testid="upload-remove-selected-btn"
          >
            {mergedMessages.removeSelected}
          </Button>
          <Button
            title={mergedMessages.clearAll}
            ariaLabel={mergedMessages.clearAll}
            onClick={handleClearAllFilesClick}
            disabled={props.disabled || selectedFiles.length === 0}
            theme={props.theme === "white" ? "light" : props.theme}
            size="small"
            buttonClassName="upload-clear-all-btn"
            data-testid="upload-clear-all-btn"
          >
            {mergedMessages.clearAll}
          </Button>
        </div>
      )}

      {selectedFiles.length > 0 && !props.autoUpload && props.onUpload && (
        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          {renderActionButtons()}
        </div>
      )}

      {props.autoUpload && props.onUpload && loading && (
        <div style={{ marginTop: 12 }}>
          <Button
            title={mergedMessages.cancel}
            onClick={handleCancelUpload}
            disabled={props.disabled}
            theme={props.theme === "white" ? "light" : props.theme}
            size={props.containerProps?.size || "medium"}
            data-testid="upload-cancel-upload-btn"
            buttonClassName="upload-cancel-upload-btn upload-cancel-upload-btn--danger"
          >
            {mergedMessages.cancel}
          </Button>
        </div>
      )}

      {/* Toast para arquivos duplicados */}
      {state.customError && state.customError.includes('já está na lista') && (
        <Toast
          type="error"
          visible={!!state.customError}
          onClose={() => state.setCustomError(null)}
        >
          {state.customError}
        </Toast>
      )}

      {hasLargeFiles && (
        <div
          className="upload-error upload-large-file-warning"
          role="alert"
          aria-live="polite"
          data-testid="upload-large-file-warning"
        >
          {mergedMessages.largeFileWarning}
        </div>
      )}

      {undoInfo && undoInfo.visible && (
        <Toast
          type="success"
          visible={undoInfo.visible}
          onClose={() => state.setUndoInfo(null)}
        >
          <span>
            {undoInfo.files.length === 1
              ? mergedMessages.fileRemoved
              : (mergedMessages.filesRemoved ?? "Arquivos removidos – {count}").replace("{count}", String(undoInfo.files.length))}
            <span
              style={{
                marginLeft: 8,
                color: "#009EEB",
                cursor: "pointer",
                textDecoration: "underline",
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
                display: "inline-block"
              }}
            >
              <Button
                title={mergedMessages.undo}
                onClick={() => {
                  // Salva índice do primeiro restaurado para focar depois
                  if (undoInfo && undoInfo.prevFiles && undoInfo.files.length > 0) {
                    const restored = undoInfo.files[0];
                    const idx = undoInfo.prevFiles.findIndex(f =>
                      f.file.name === restored.file.name && f.file.size === restored.file.size
                    );
                    restoredIndexRef.current = idx;
                  }
                  handleUndoRemove(); // <-- use direto
                  state.setUndoInfo(null);
                }}
                buttonClassName="toast-undo"
                size="tiny"
                theme="light"
              >
                {mergedMessages.undo}
              </Button>
            </span>
          </span>
        </Toast>
      )}
    </div>
  );
});

Upload.displayName = "Upload";

export default Upload as React.ForwardRefExoticComponent<
  UploadProps & React.RefAttributes<UploadImperativeHandle>
>;

export interface UploadFileWithPath {
  file: File;
  relativePath?: string;
  customName?: string; // <-- já existe
}

/**
 * @property {(args: {file: File, index?: number, response?: any}) => Promise<void>} [onDownload] - Callback chamado ao clicar para baixar um arquivo. Se não definido, faz download local.
 * @property {(args: {file: File, index?: number, response?: any}) => Promise<void>} [onDelete] - Callback chamado ao clicar para excluir um arquivo do backend. Após sucesso, remove localmente.
 */

// Exporte os tipos auxiliares para uso externo
export type { UploadProps, UploadMessages };

